<template>
  <div class="logo-page">

    <!-- ===== HERO ===== -->
    <section class="logo-hero">
      <div class="aurora" aria-hidden="true">
        <div class="orb o1"></div>
        <div class="orb o2"></div>
        <div class="orb o3"></div>
      </div>
      <div class="dot-grid" aria-hidden="true"></div>

      <div class="container hero-inner">
        <div class="hero-label">Brand Identity</div>
        <h1 class="hero-title">The Logo</h1>
        <p class="hero-sub">
          A single mark that tells the story of code, curiosity, and the
          constant pull toward the horizon.
        </p>

        <!-- Large logo display -->
        <div class="logo-showcase">
          <svg class="big-logo" viewBox="-12 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="bg1" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%"   stop-color="#5b8fff"/>
                <stop offset="100%" stop-color="#a78bfa"/>
              </linearGradient>
              <linearGradient id="bg2" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%"   stop-color="#a0c8ff"/>
                <stop offset="100%" stop-color="#d4b8ff"/>
              </linearGradient>
            </defs>

            <circle cx="4"  cy="4" r="3" fill="#5b8fff"/>
            <circle cx="48" cy="4" r="3" fill="#a78bfa"/>

            <line x1="2.3"  y1="5"  x2="24.3" y2="43" stroke="url(#bg2)" stroke-width="2.2" stroke-linecap="round"/>
            <line x1="5.7"  y1="3"  x2="27.7" y2="41" stroke="url(#bg1)" stroke-width="2.2" stroke-linecap="round"/>
            <line x1="46.3" y1="3"  x2="24.3" y2="41" stroke="url(#bg1)" stroke-width="2.2" stroke-linecap="round"/>
            <line x1="49.7" y1="5"  x2="27.7" y2="43" stroke="url(#bg2)" stroke-width="2.2" stroke-linecap="round"/>

            <text x="-9" y="24" font-family="monospace" font-size="9" font-weight="700" fill="#5b8fff" opacity="0.9">&lt;/&gt;</text>

            <circle cx="41.8" cy="14.6" r="8"   fill="none" stroke="#a78bfa" stroke-width="1.1" stroke-dasharray="2.4 3" opacity="0.6"/>
            <ellipse cx="41.8" cy="14.6" rx="8" ry="3.2" fill="none" stroke="#a78bfa" stroke-width="0.7" stroke-dasharray="1.5 2.5" opacity="0.25"/>

            <g>
              <path d="M49.8,18.2 L52.8,11.2 L49.8,13 L46.8,11.2 Z" fill="#e8b84b"/>
              <animateTransform attributeName="transform" type="rotate"
                from="0 41.8 14.6" to="360 41.8 14.6" dur="4.5s" repeatCount="indefinite"/>
            </g>
          </svg>

          <div class="logo-wordmark">vidya</div>
        </div>
      </div>
    </section>

    <!-- ===== BREAKDOWN ===== -->
    <section class="breakdown-section">
      <div class="container">
        <div class="section-header reveal">
          <div class="section-tag">Design Language</div>
          <h2 class="sec-title">Every element<br>has a meaning.</h2>
        </div>

        <!-- Element cards -->
        <div class="elements">

          <!-- 1. The V -->
          <div class="element-row reveal">
            <div class="element-visual">
              <svg viewBox="2 0 48 48" fill="none">
                <defs>
                  <linearGradient id="ev1" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stop-color="#5b8fff"/>
                    <stop offset="100%" stop-color="#a78bfa"/>
                  </linearGradient>
                  <linearGradient id="ev2" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stop-color="#a0c8ff"/>
                    <stop offset="100%" stop-color="#d4b8ff"/>
                  </linearGradient>
                </defs>
                <line x1="2.3"  y1="5"  x2="24.3" y2="43" stroke="url(#ev2)" stroke-width="2.2" stroke-linecap="round"/>
                <line x1="5.7"  y1="3"  x2="27.7" y2="41" stroke="url(#ev1)" stroke-width="2.2" stroke-linecap="round"/>
                <line x1="46.3" y1="3"  x2="24.3" y2="41" stroke="url(#ev1)" stroke-width="2.2" stroke-linecap="round"/>
                <line x1="49.7" y1="5"  x2="27.7" y2="43" stroke="url(#ev2)" stroke-width="2.2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="element-text">
              <div class="el-number">01</div>
              <h3>The Double-line V</h3>
              <p>
                <strong>V</strong> is the initial — but the double line transforms it into something more.
                Two parallel paths running side by side represent the two worlds I live in:
                <span class="accent-blue">technology</span> and <span class="accent-purple">exploration</span>.
                A vivid shade and a lighter shade — like a road lit by daylight and moonlight,
                always heading somewhere new.
              </p>
            </div>
          </div>

          <!-- 2. Terminal dots -->
          <div class="element-row reveal reveal-delay-1 row-reverse">
            <div class="element-visual">
              <svg viewBox="0 0 52 16" fill="none">
                <circle cx="4"  cy="8" r="4" fill="#5b8fff"/>
                <line x1="0" y1="8" x2="4" y2="8" stroke="#5b8fff" stroke-width="2" stroke-linecap="round" opacity="0.5"/>
                <line x1="4" y1="8" x2="22" y2="8" stroke="url(#et1)" stroke-width="2" stroke-linecap="round" opacity="0.3"/>
                <circle cx="48" cy="8" r="4" fill="#a78bfa"/>
                <line x1="48" y1="8" x2="52" y2="8" stroke="#a78bfa" stroke-width="2" stroke-linecap="round" opacity="0.5"/>
                <line x1="30" y1="8" x2="48" y2="8" stroke="url(#et2)" stroke-width="2" stroke-linecap="round" opacity="0.3"/>
                <defs>
                  <linearGradient id="et1" x1="4" y1="0" x2="22" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#5b8fff"/>
                    <stop offset="100%" stop-color="#5b8fff" stop-opacity="0"/>
                  </linearGradient>
                  <linearGradient id="et2" x1="30" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#a78bfa" stop-opacity="0"/>
                    <stop offset="100%" stop-color="#a78bfa"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div class="element-text">
              <div class="el-number">02</div>
              <h3>Circuit Terminals</h3>
              <p>
                The two filled dots at the top of each arm are <span class="accent-blue">circuit board terminal nodes</span> —
                the kind you find at the start and end of a signal trace.
                They also read as departure points on a map, where every journey begins.
                Tech and travel, speaking the same visual language.
              </p>
            </div>
          </div>

          <!-- 3. </> -->
          <div class="element-row reveal reveal-delay-2">
            <div class="element-visual">
              <svg viewBox="-4 0 40 30" fill="none">
                <text x="0" y="22" font-family="monospace" font-size="22" font-weight="700"
                      fill="#5b8fff" opacity="0.95">&lt;/&gt;</text>
              </svg>
            </div>
            <div class="element-text">
              <div class="el-number">03</div>
              <h3>The Code Tag &lt;/&gt;</h3>
              <p>
                Placed on the left arm — the <span class="accent-blue">tech arm</span>.
                <code>&lt;/&gt;</code> is the universal closing tag of web development.
                It signals that I am, at the core, a builder. Every pixel, every system,
                every AI pipeline I've shipped started with opening a tag and closing it clean.
                This is where the engineer lives.
              </p>
            </div>
          </div>

          <!-- 4. Orbit + Plane -->
          <div class="element-row reveal reveal-delay-1 row-reverse">
            <div class="element-visual">
              <svg viewBox="30 4 24 24" fill="none">
                <circle cx="41.8" cy="14.6" r="8" fill="none" stroke="#a78bfa"
                        stroke-width="1.1" stroke-dasharray="2.4 3" opacity="0.7"/>
                <ellipse cx="41.8" cy="14.6" rx="8" ry="3.2" fill="none" stroke="#a78bfa"
                         stroke-width="0.8" stroke-dasharray="1.5 2.5" opacity="0.3"/>
                <g>
                  <path d="M49.8,18.2 L52.8,11.2 L49.8,13 L46.8,11.2 Z" fill="#e8b84b"/>
                  <animateTransform attributeName="transform" type="rotate"
                    from="0 41.8 14.6" to="360 41.8 14.6" dur="4.5s" repeatCount="indefinite"/>
                </g>
              </svg>
            </div>
            <div class="element-text">
              <div class="el-number">04</div>
              <h3>The Orbit & The Plane</h3>
              <p>
                On the right arm — the <span class="accent-purple">explorer arm</span> —
                a paper plane orbits an elliptical ring.
                The dashed circle evokes a <span class="accent-gold">flight path</span>,
                a satellite orbit, a globe's latitude line.
                The plane never stops moving — because I never stop moving.
                Always in transit, always curious, always chasing the next horizon.
              </p>
            </div>
          </div>

          <!-- 5. Gradient -->
          <div class="element-row reveal reveal-delay-2">
            <div class="element-visual grad-swatch">
              <div class="swatch-bar"></div>
              <div class="swatch-labels">
                <span style="color: #5b8fff">Blue</span>
                <span style="color: #a78bfa">Purple</span>
              </div>
            </div>
            <div class="element-text">
              <div class="el-number">05</div>
              <h3>Blue to Purple Gradient</h3>
              <p>
                <span class="accent-blue">Electric blue</span> on the left: logic, code, systems, precision.
                <span class="accent-purple">Soft purple</span> on the right: creativity, imagination, the unknown.
                The gradient doesn't choose between them — it blends.
                Because the best work happens exactly where structure meets intuition.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- ===== FULL MARK ===== -->
    <section class="full-mark-section">
      <div class="container">
        <div class="full-mark-inner reveal">
          <div class="full-mark-text">
            <div class="section-tag">The Complete Mark</div>
            <h2>One symbol.<br>Three identities.</h2>
            <div class="identity-list">
              <div class="identity-item">
                <span class="id-icon" style="color: var(--primary)">⌨</span>
                <div>
                  <strong>Engineer</strong>
                  <p>6+ years building AI systems, full-stack products, and cloud infrastructure.</p>
                </div>
              </div>
              <div class="identity-item">
                <span class="id-icon" style="color: var(--gold)">✈</span>
                <div>
                  <strong>Traveller</strong>
                  <p>Constantly moving, exploring new places, cultures, and perspectives.</p>
                </div>
              </div>
              <div class="identity-item">
                <span class="id-icon" style="color: var(--purple)">⛰</span>
                <div>
                  <strong>Adventurer</strong>
                  <p>Drawn to the unknown — whether it's a new technology or a new trail.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="full-mark-logo">
            <svg viewBox="-12 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="fm1" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%"   stop-color="#5b8fff"/>
                  <stop offset="100%" stop-color="#a78bfa"/>
                </linearGradient>
                <linearGradient id="fm2" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%"   stop-color="#a0c8ff"/>
                  <stop offset="100%" stop-color="#d4b8ff"/>
                </linearGradient>
              </defs>
              <circle cx="4"  cy="4" r="3" fill="#5b8fff"/>
              <circle cx="48" cy="4" r="3" fill="#a78bfa"/>
              <line x1="2.3"  y1="5"  x2="24.3" y2="43" stroke="url(#fm2)" stroke-width="2.2" stroke-linecap="round"/>
              <line x1="5.7"  y1="3"  x2="27.7" y2="41" stroke="url(#fm1)" stroke-width="2.2" stroke-linecap="round"/>
              <line x1="46.3" y1="3"  x2="24.3" y2="41" stroke="url(#fm1)" stroke-width="2.2" stroke-linecap="round"/>
              <line x1="49.7" y1="5"  x2="27.7" y2="43" stroke="url(#fm2)" stroke-width="2.2" stroke-linecap="round"/>
              <text x="-9" y="24" font-family="monospace" font-size="9" font-weight="700" fill="#5b8fff" opacity="0.9">&lt;/&gt;</text>
              <circle cx="41.8" cy="14.6" r="8" fill="none" stroke="#a78bfa" stroke-width="1.1" stroke-dasharray="2.4 3" opacity="0.6"/>
              <ellipse cx="41.8" cy="14.6" rx="8" ry="3.2" fill="none" stroke="#a78bfa" stroke-width="0.7" stroke-dasharray="1.5 2.5" opacity="0.25"/>
              <g>
                <path d="M49.8,18.2 L52.8,11.2 L49.8,13 L46.8,11.2 Z" fill="#e8b84b"/>
                <animateTransform attributeName="transform" type="rotate"
                  from="0 41.8 14.6" to="360 41.8 14.6" dur="4.5s" repeatCount="indefinite"/>
              </g>
            </svg>
            <div class="mark-wordmark">vidya</div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

let observer: IntersectionObserver | null = null

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible') }),
    { threshold: 0.1 }
  )
  document.querySelectorAll('.reveal').forEach(el => observer!.observe(el))
})

onUnmounted(() => { observer?.disconnect() })
</script>

<style scoped>
.logo-page { padding-top: 80px; }

/* ===== HERO ===== */
.logo-hero {
  position: relative;
  min-height: 80vh;
  display: flex;
  align-items: center;
  padding: var(--space-20) 0 var(--space-16);
  overflow: hidden;
}

.aurora { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.orb {
  position: absolute; border-radius: 50%;
  filter: blur(110px); opacity: 0.12;
}
.o1 { width: 600px; height: 600px; background: var(--primary); top: -200px; right: -100px; animation: d1 12s ease-in-out infinite; }
.o2 { width: 400px; height: 400px; background: var(--purple); bottom: -100px; left: -80px; animation: d2 10s ease-in-out infinite; }
.o3 { width: 300px; height: 300px; background: var(--gold); top: 40%; left: 30%; opacity: 0.06; animation: d3 14s ease-in-out infinite; }
@keyframes d1 { 0%,100%{transform:translate(0,0)}50%{transform:translate(-20px,20px)} }
@keyframes d2 { 0%,100%{transform:translate(0,0)}50%{transform:translate(16px,-16px)} }
@keyframes d3 { 0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-12px,-20px) scale(1.08)} }

.dot-grid {
  position: absolute; inset: 0;
  background-image: radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 48px 48px; pointer-events: none;
}
[data-theme="light"] .dot-grid {
  background-image: radial-gradient(circle, rgba(0,0,0,0.05) 1px, transparent 1px);
}

.hero-inner {
  position: relative; z-index: 1;
  display: flex; flex-direction: column;
  align-items: center; text-align: center;
  gap: var(--space-6);
}

.hero-label {
  font-family: var(--font-display);
  font-size: 0.76rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 1px;
  color: var(--primary);
  padding: 5px 14px;
  border: 1px solid rgba(91,143,255,0.25);
  border-radius: var(--radius-full);
  background: var(--primary-dim);
}

.hero-title {
  font-size: clamp(3rem, 7vw, 6rem);
  font-weight: 700;
  letter-spacing: -0.04em;
  background: linear-gradient(135deg, var(--primary), var(--purple));
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}

.hero-sub {
  font-size: 1.1rem; color: var(--text-muted);
  max-width: 520px; line-height: 1.75;
}

.logo-showcase {
  display: flex; flex-direction: column;
  align-items: center; gap: var(--space-4);
  margin-top: var(--space-8);
  padding: var(--space-12) var(--space-16);
  background: rgba(255,255,255,0.02);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-2xl);
  backdrop-filter: blur(10px);
}

[data-theme="light"] .logo-showcase { background: rgba(255,255,255,0.6); }

.big-logo {
  width: 180px; height: auto;
  overflow: visible;
  filter: drop-shadow(0 0 24px rgba(91,143,255,0.2));
  animation: float-logo 6s ease-in-out infinite;
}

@keyframes float-logo {
  0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)}
}

.logo-wordmark {
  font-family: var(--font-display);
  font-size: 2.2rem; font-weight: 700;
  letter-spacing: -0.04em;
  color: var(--text);
}

/* ===== BREAKDOWN ===== */
.breakdown-section { padding: var(--space-24) 0; }

.section-header { text-align: center; margin-bottom: var(--space-16); }

.sec-title {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700; letter-spacing: -0.03em;
  line-height: 1.15;
}

.elements { display: flex; flex-direction: column; gap: var(--space-6); }

.element-row {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: var(--space-12);
  align-items: center;
  padding: var(--space-10) var(--space-10);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  transition: border-color var(--transition-normal), box-shadow var(--transition-normal);
}

.element-row:hover {
  border-color: var(--border-light);
  box-shadow: var(--shadow-lg);
}

.element-row.row-reverse {
  grid-template-columns: 1fr 240px;
}
.element-row.row-reverse .element-visual { order: 2; }
.element-row.row-reverse .element-text   { order: 1; }

.element-visual {
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  min-height: 120px;
}

[data-theme="light"] .element-visual { background: rgba(0,0,0,0.02); }

.element-visual svg { width: 100%; max-width: 160px; height: auto; overflow: visible; }

.grad-swatch {
  flex-direction: column; gap: var(--space-3);
}

.swatch-bar {
  width: 100%; height: 12px;
  border-radius: var(--radius-full);
  background: linear-gradient(90deg, #5b8fff, #a78bfa);
  box-shadow: 0 0 20px rgba(91,143,255,0.3);
}

.swatch-labels {
  display: flex; justify-content: space-between;
  font-family: var(--font-display); font-size: 0.78rem; font-weight: 600;
  width: 100%;
}

.el-number {
  font-family: var(--font-display);
  font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 1px;
  color: var(--text-dim);
  margin-bottom: var(--space-3);
}

.element-text h3 {
  font-size: 1.3rem; font-weight: 700;
  margin-bottom: var(--space-4);
  letter-spacing: -0.01em;
}

.element-text p {
  font-size: 0.95rem; line-height: 1.75;
  color: var(--text-muted);
}

.element-text strong { color: var(--text); font-weight: 600; }
.element-text code {
  font-family: monospace;
  font-size: 0.9em;
  color: var(--primary);
  background: var(--primary-dim);
  padding: 2px 6px;
  border-radius: 4px;
}

.accent-blue   { color: var(--primary);  font-weight: 600; }
.accent-purple { color: var(--purple);   font-weight: 600; }
.accent-gold   { color: var(--gold);     font-weight: 600; }

/* ===== FULL MARK ===== */
.full-mark-section { padding: var(--space-24) 0 var(--space-16); }

.full-mark-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-16);
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-2xl);
  padding: var(--space-16);
  position: relative;
  overflow: hidden;
}

.full-mark-inner::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(circle at 0% 50%, var(--primary-dim), transparent 55%);
  pointer-events: none;
}

.full-mark-text {
  display: flex; flex-direction: column; gap: var(--space-8);
  position: relative; z-index: 1;
}

.full-mark-text h2 {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700; letter-spacing: -0.03em; line-height: 1.2;
}

.identity-list {
  display: flex; flex-direction: column; gap: var(--space-5);
}

.identity-item {
  display: flex; align-items: flex-start; gap: var(--space-4);
}

.id-icon {
  font-size: 1.4rem; line-height: 1; flex-shrink: 0;
  margin-top: 2px;
}

.identity-item strong {
  display: block;
  font-family: var(--font-display); font-size: 1rem;
  font-weight: 600; color: var(--text);
  margin-bottom: 3px;
}

.identity-item p {
  font-size: 0.88rem; line-height: 1.65;
  margin: 0;
}

.full-mark-logo {
  display: flex; flex-direction: column;
  align-items: center; gap: var(--space-5);
  position: relative; z-index: 1;
}

.full-mark-logo svg {
  width: 200px; height: auto; overflow: visible;
  filter: drop-shadow(0 0 32px rgba(91,143,255,0.25));
}

.mark-wordmark {
  font-family: var(--font-display);
  font-size: 2.4rem; font-weight: 700;
  letter-spacing: -0.04em; color: var(--text);
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .logo-page { padding-top: 70px; }

  .logo-showcase { padding: var(--space-8); }
  .big-logo { width: 130px; }

  .element-row,
  .element-row.row-reverse {
    grid-template-columns: 1fr;
    gap: var(--space-6);
    padding: var(--space-8);
  }
  .element-row.row-reverse .element-visual,
  .element-row.row-reverse .element-text { order: unset; }

  .full-mark-inner { grid-template-columns: 1fr; padding: var(--space-10) var(--space-8); }
  .full-mark-logo svg { width: 150px; }
}
</style>
